import { useEffect, useState } from 'react';
import { lookupsAPI } from '../services/api';
import { LookupItem } from '../types';

const CACHE_TTL_MS = 0; // Always refresh on mount while sharing in-flight requests

let cachedCategories: LookupItem[] | null = null;
let cacheTimestamp = 0;
let pendingRequest: Promise<LookupItem[]> | null = null;

export function useTransactionCategories() {
  const [categories, setCategories] = useState<LookupItem[]>(cachedCategories ?? []);
  const [loading, setLoading] = useState<boolean>(!cachedCategories);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    if (cachedCategories) {
      setCategories(cachedCategories);
      setLoading(false);
    }

    const cacheIsStale = !cachedCategories || (Date.now() - cacheTimestamp) >= CACHE_TTL_MS;

    if (!cacheIsStale && cachedCategories) {
      return () => {
        isActive = false;
      };
    }

    if (!pendingRequest) {
      pendingRequest = lookupsAPI.getTransactionCategories()
        .then((result) => {
          cachedCategories = result;
          cacheTimestamp = Date.now();
          pendingRequest = null;
          return result;
        })
        .catch((requestError) => {
          pendingRequest = null;
          throw requestError;
        });
    }

    pendingRequest
      ?.then((result) => {
        if (!isActive) {
          return;
        }
        setCategories(result);
        setLoading(false);
      })
      .catch((requestError) => {
        if (!isActive) {
          return;
        }
        const normalizedError = requestError instanceof Error
          ? requestError
          : new Error('Failed to load transaction categories');
        setError(normalizedError);
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  return { categories, loading, error };
}
